import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import { PageShell } from '../shared/PageShell';
import { startBackgroundAnalysis } from '../../services/orchestrator/analysisOrchestrator';
import { saveConsentRecord } from '../../services/firestore/dataopsService';
import { getSessionId } from '../../utils/sessionId';
import { getAuthUid } from '../../services/firebase';
import { createJourney } from '../../services/firestore/journeyService';
import { atomicTriageSafetyWrite, buildConsentState } from '../../services/nurse/triageFirestoreWrite';
import { logAudit } from '../../utils/auditLog';
import { isClientBlocked, checkAgeGate, calculateAgeFromDob } from '../../services/nurse/ageGateClient';
import type { ConsentPurpose } from '../../types/dataops';

/** Format raw digits into MM/DD/YYYY as user types */
function formatDobInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Parse MM/DD/YYYY → ISO date string (YYYY-MM-DD) or null */
function parseDob(display: string): string | null {
  const digits = display.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const mm = digits.slice(0, 2);
  const dd = digits.slice(2, 4);
  const yyyy = digits.slice(4);
  const m = parseInt(mm, 10);
  const d = parseInt(dd, 10);
  const y = parseInt(yyyy, 10);
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900) return null;
  return `${yyyy}-${mm}-${dd}`;
}

// ── Consent definitions ──
interface ConsentDef {
  purpose: ConsentPurpose;
  label: string;
  text: string;
  required: boolean;
}

const CONSENTS: ConsentDef[] = [
  // ── Required (session blocked if either false) [NRS.02 D#3 c1] ──
  {
    purpose: 'terms_of_service',
    label: 'Terms of Service',
    text: 'I have read and agree to the HemoLogix Terms of Service.',
    required: true,
  },
  {
    purpose: 'data_processing',
    label: 'Data Processing Authorization',
    text: 'I authorize HemoLogix to process my health data, including uploaded lab results and profile information, for the purpose of providing personalized health insights.',
    required: true,
  },
  // ── Optional (withdrawable post-session via FLO.07) [NRS.02 D#3 c2, c5] ──
  {
    purpose: 'research_participation',
    label: 'Research Participation',
    text: 'I consent to my anonymized data being used for health research and platform improvement studies.',
    required: false,
  },
  {
    purpose: 'marketing_communications',
    label: 'Marketing Communications',
    text: 'I consent to receiving educational content, product updates, and promotional offers via email.',
    required: false,
  },
  {
    purpose: 'clinical_data_sharing',
    label: 'Clinical Data Sharing',
    text: 'I consent to sharing my clinical data with healthcare providers involved in my care for treatment coordination.',
    required: false,
  },
];

export function TriageScreen() {
  const { state, dispatch } = useApp();
  const [step, setStep] = useState<'profile' | 'pregnancy' | 'consent'>('profile');
  const [sex, setSex] = useState<'female' | 'male' | null>(null);
  const [dobDisplay, setDobDisplay] = useState('');
  const [error, setError] = useState('');
  const [blocked, setBlocked] = useState(false);

  // Consent state — ALL start unchecked (GDPR: affirmative action required, no pre-checked boxes)
  const [consents, setConsents] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const c of CONSENTS) {
      init[c.purpose] = false;
    }
    return init;
  });
  const [consentError, setConsentError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // [c1] Check client-side age block on mount (localStorage cooldown)
  // [c4] If localStorage unavailable, isClientBlocked() logs warning and returns false
  useEffect(() => {
    if (isClientBlocked()) {
      setBlocked(true);
      logAudit('auth.failed', 'age_gate', { reason: 'returning_blocked_user' });
    }
  }, []);

  function handleDobChange(value: string) {
    setDobDisplay(formatDobInput(value));
    setError('');
  }

  async function handleProfileSubmit() {
    if (!sex) {
      setError('Please select your biological sex.');
      return;
    }
    if (!dobDisplay) {
      setError('Please enter your date of birth.');
      return;
    }

    const iso = parseDob(dobDisplay);
    if (!iso) {
      setError('Please enter a valid date (MM/DD/YYYY).');
      return;
    }

    // Quick client-side sanity check
    const clientAge = calculateAgeFromDob(iso);
    if (clientAge > 120) {
      setError('Please enter a valid date of birth.');
      return;
    }

    // [c1][c2] Full age gate: client-side immediate + server-side authoritative
    setSubmitting(true);
    setError('');
    const sessionId = getSessionId();

    try {
      const gateResult = await checkAgeGate(iso, sessionId);

      if (!gateResult.allowed) {
        setBlocked(true);
        logAudit('auth.failed', 'age_gate', {
          reason: gateResult.reason ?? 'under_18',
          age_entered: gateResult.calculatedAge ?? clientAge,
          server_verified: gateResult.serverVerified,
        });
        setSubmitting(false);
        return;
      }

      const verifiedAge = gateResult.calculatedAge ?? clientAge;

      // Save triage data and route to next step
      dispatch({ type: 'SET_TRIAGE_DATA', payload: { sex, dateOfBirth: iso, age: verifiedAge } });
      logAudit('auth.login', 'age_gate', { age: verifiedAge, sex });
      // Female patients get pregnancy screening before consent
      setStep(sex === 'female' ? 'pregnancy' : 'consent');
    } catch {
      // Server error — fail-closed
      setError('Unable to verify your information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function handlePregnancySubmit(status: NonNullable<import('../../types').TriageData['reproductiveStatus']>) {
    const triageData = state.triageData;
    if (triageData) {
      dispatch({ type: 'SET_TRIAGE_DATA', payload: { ...triageData, reproductiveStatus: status } });
    }
    logAudit('consent.granted', 'pregnancy_screening', { status });
    setStep('consent');
  }

  async function handleConsentSubmit() {
    // Validate required consents
    const missingRequired = CONSENTS.filter((c) => c.required && !consents[c.purpose]);
    if (missingRequired.length > 0) {
      setConsentError('Please accept the required consents to continue.');
      return;
    }

    setSubmitting(true);
    setConsentError('');

    // [c5] Validate all triage fields before Firestore write (no partial writes)
    const triageCheck = state.triageData;
    if (!triageCheck || !triageCheck.sex || !triageCheck.dateOfBirth || triageCheck.age === undefined) {
      setConsentError('Your profile information is incomplete. Please go back and try again.');
      setSubmitting(false);
      return;
    }
    if (triageCheck.sex === 'female' && !triageCheck.reproductiveStatus) {
      setConsentError('Reproductive status is required. Please go back and try again.');
      setSubmitting(false);
      return;
    }

    const sessionId = getSessionId();
    const now = new Date().toISOString();

    // Save consent records for all checked purposes
    const consentPromises = CONSENTS
      .filter((c) => consents[c.purpose])
      .map((c) =>
        saveConsentRecord({
          id: `consent-${sessionId.slice(0, 8)}-${c.purpose}-${Date.now()}`,
          userId: sessionId,
          purpose: c.purpose,
          action: 'granted',
          policyVersion: '1.0.0',
          consentText: c.text,
          method: 'checkbox',
          ipAddress: null,
          userAgent: navigator.userAgent,
          timestamp: now,
          expiresAt: null,
          withdrawalReason: null,
          effectiveImmediately: true,
        }).catch(() => {
          // Don't block user flow on consent save failure
        }),
      );

    await Promise.all(consentPromises);

    // Log audit events
    for (const c of CONSENTS) {
      if (consents[c.purpose]) {
        logAudit('consent.granted', c.purpose, { method: 'checkbox', policy_version: '1.0.0' });
      }
    }

    // Persist journey to Firestore (fire-and-forget)
    const triageData = state.triageData;
    if (triageData) {
      getAuthUid()
        .then((uid) => createJourney(sessionId, triageData, uid))
        .catch(() => createJourney(sessionId, triageData, null))
        .catch((err) => console.warn('[Journey] Create failed:', err));

      // [NRS.02 D#3 c3/c4] Build structured consent state with submission timestamp
      const consentState = buildConsentState(consents, now);

      // NRS.02 D#4: Atomic write TriageData + DataCompletenessFlags + ConsentState to safetyProfile
      // Sets sexConfirmed=true, birthDateConfirmed=true; emits session_created event for NRS.08
      // Retries 3x with exponential backoff (1s/2s/4s); shows actionable error on failure
      try {
        await atomicTriageSafetyWrite(sessionId, triageData, consentState);
      } catch (safetyErr) {
        // Surface actionable error — do not silently swallow
        setConsentError(
          safetyErr instanceof Error
            ? safetyErr.message
            : 'Unable to save your safety profile. Please try again.',
        );
        setSubmitting(false);
        return;
      }
    }

    // Start background lab analysis (if a file was uploaded)
    const file = window.__hemologixUpload;
    if (file) {
      delete window.__hemologixUpload;
      dispatch({ type: 'SET_ANALYSIS_STATUS', payload: 'running' });
      startBackgroundAnalysis(file, state.mockMode);
      logAudit('data.view', 'lab_upload', { has_file: true });
    }

    dispatch({ type: 'SET_SCREEN', payload: 'chat' });
  }

  // ── BLOCKED STATE ──
  if (blocked) {
    return (
      <PageShell>
        <div className="relative flex flex-col items-center justify-center min-h-screen px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center text-center max-w-md w-full"
          >
            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-6">
              <span className="text-3xl">🔒</span>
            </div>
            <h1 className="font-display text-2xl font-bold tracking-wide text-accent">
              Age Requirement Not Met
            </h1>
            <p className="mt-4 text-text-secondary text-base leading-relaxed">
              HemoLogix requires all users to be 18 years of age or older.
              This is required by our Terms of Service and applicable health data privacy regulations.
            </p>
            <p className="mt-4 text-text-muted text-sm">
              If you believe this is an error, please contact{' '}
              <a href="mailto:support@hemologix.com" className="text-accent-light hover:underline">
                support@hemologix.com
              </a>
            </p>
          </motion.div>
        </div>
      </PageShell>
    );
  }

  // ── PREGNANCY SCREENING STEP (female only) ──
  if (step === 'pregnancy') {
    const pregnancyOptions: { value: NonNullable<import('../../types').TriageData['reproductiveStatus']>; label: string }[] = [
      { value: 'pregnant', label: "Yes, I\u2019m pregnant" },
      { value: 'trying_to_conceive', label: 'Trying to conceive' },
      { value: 'breastfeeding', label: 'Breastfeeding' },
      { value: 'postmenopausal', label: 'Postmenopausal' },
    ];

    return (
      <PageShell>
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, var(--accent-glow) 0%, transparent 70%)' }}
          />
        </div>

        <div className="relative flex flex-col items-center justify-center min-h-screen px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center text-center max-w-md w-full"
          >
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-wide">
              One More Thing
            </h1>
            <p className="mt-3 text-text-secondary text-base leading-relaxed">
              Certain biological factors require specialized consideration during pregnancy or while breastfeeding.
              This helps us ensure your personalized analysis remains safe.
            </p>

            <div className="mt-8 w-full flex flex-col gap-3">
              {pregnancyOptions.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => handlePregnancySubmit(value)}
                  className="w-full py-4 rounded-xl text-base font-semibold transition-all bg-bg-card border-2 border-bg-tertiary text-text-secondary hover:border-accent-light/40"
                >
                  {label}
                </button>
              ))}
              <button
                onClick={() => handlePregnancySubmit('not_applicable')}
                className="mt-2 w-full py-4 rounded-xl bg-accent text-bg-deep text-base font-bold hover:bg-accent-light transition-colors"
              >
                None of these apply
              </button>
            </div>

            <p className="mt-6 text-text-muted text-sm">
              This information is used only for personalized safety screening.
            </p>
          </motion.div>
        </div>
      </PageShell>
    );
  }

  // ── CONSENT STEP ──
  if (step === 'consent') {
    const requiredChecked = CONSENTS.filter((c) => c.required).every((c) => consents[c.purpose]);

    return (
      <PageShell>
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, var(--accent-glow) 0%, transparent 70%)' }}
          />
        </div>

        <div className="relative flex flex-col items-center justify-center min-h-screen px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center text-center max-w-lg w-full"
          >
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-wide">
              Data Consent
            </h1>
            <p className="mt-3 text-text-secondary text-base leading-relaxed">
              Before we analyze your lab results, please review how we use your data.
            </p>

            <div className="mt-8 w-full text-left space-y-4">
              {CONSENTS.map((c) => (
                <label
                  key={c.purpose}
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    consents[c.purpose]
                      ? 'bg-accent/5 border-accent-light/40'
                      : 'bg-bg-card border-bg-tertiary hover:border-text-muted'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={consents[c.purpose]}
                    onChange={(e) => {
                      setConsents((prev) => ({ ...prev, [c.purpose]: e.target.checked }));
                      setConsentError('');
                    }}
                    className="mt-1 w-5 h-5 rounded accent-accent-light flex-shrink-0"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-text-primary font-medium text-base">{c.label}</span>
                      {c.required && (
                        <span className="text-xs font-semibold text-accent-light bg-accent/10 px-2 py-0.5 rounded-full">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-text-muted text-sm mt-1 leading-relaxed">{c.text}</p>
                  </div>
                </label>
              ))}
            </div>

            {consentError && (
              <p className="mt-4 text-accent text-base">{consentError}</p>
            )}

            <button
              onClick={handleConsentSubmit}
              disabled={!requiredChecked || submitting}
              className="mt-8 w-full py-4 rounded-xl bg-accent text-bg-deep text-lg font-bold hover:bg-accent-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Starting...' : 'Accept & Start Analysis'}
            </button>

            <p className="mt-4 text-text-muted text-sm">
              By proceeding, you agree to our{' '}
              <a href="/privacy" className="text-accent-light hover:underline">Privacy Policy</a>
              {' '}and{' '}
              <a href="/terms" className="text-accent-light hover:underline">Terms of Service</a>.
            </p>
            <p className="mt-2 text-text-muted text-xs leading-relaxed">
              Optional consents can be withdrawn at any time through your patient portal.
            </p>
          </motion.div>
        </div>
      </PageShell>
    );
  }

  // ── PROFILE STEP (DOB + Sex) ──
  return (
    <PageShell>
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, var(--accent-glow) 0%, transparent 70%)' }}
        />
      </div>

      <div className="relative flex flex-col items-center justify-center min-h-screen px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center text-center max-w-md w-full"
        >
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-wide">
            Quick Profile
          </h1>
          <p className="mt-3 text-text-secondary text-base md:text-lg leading-relaxed">
            We need a couple details to personalize your analysis.
          </p>

          {/* Sex selection */}
          <div className="mt-8 w-full">
            <p className="text-text-secondary text-base mb-3">Biological Sex</p>
            <div className="flex gap-3">
              <button
                onClick={() => { setSex('female'); setError(''); }}
                className={`flex-1 py-4 rounded-xl text-lg font-semibold transition-all ${
                  sex === 'female'
                    ? 'bg-accent/20 border-2 border-accent-light text-accent-light'
                    : 'bg-bg-card border-2 border-bg-tertiary text-text-secondary hover:border-text-muted'
                }`}
              >
                Female
              </button>
              <button
                onClick={() => { setSex('male'); setError(''); }}
                className={`flex-1 py-4 rounded-xl text-lg font-semibold transition-all ${
                  sex === 'male'
                    ? 'bg-accent/20 border-2 border-accent-light text-accent-light'
                    : 'bg-bg-card border-2 border-bg-tertiary text-text-secondary hover:border-text-muted'
                }`}
              >
                Male
              </button>
            </div>
          </div>

          {/* DOB input */}
          <div className="mt-6 w-full">
            <label className="block text-text-secondary text-base mb-3" htmlFor="dob">
              Date of Birth
            </label>
            <input
              id="dob"
              type="text"
              inputMode="numeric"
              placeholder="MM/DD/YYYY"
              value={dobDisplay}
              onChange={(e) => handleDobChange(e.target.value)}
              className="w-full px-4 py-4 rounded-xl bg-bg-card border-2 border-bg-tertiary text-text-primary text-lg text-center tracking-widest focus:border-accent-light focus:outline-none transition-colors"
              maxLength={10}
            />
          </div>

          {/* Error */}
          {error && (
            <p className="mt-4 text-accent text-base">{error}</p>
          )}

          {/* Submit */}
          <button
            onClick={handleProfileSubmit}
            disabled={submitting}
            className="mt-8 w-full py-4 rounded-xl bg-accent text-bg-deep text-lg font-bold hover:bg-accent-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Verifying...' : 'Continue'}
          </button>

          <p className="mt-4 text-text-muted text-sm">
            Your data is processed securely under our{' '}
            <a href="/privacy" className="text-accent-light hover:underline">Privacy Policy</a>.
            You must be 18 or older to use HemoLogix.
          </p>
        </motion.div>
      </div>
    </PageShell>
  );
}
